import { useAuth } from "@clerk/clerk-react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import { ActionItemsContext } from "contexts/ActionItems";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachContactsContext } from "contexts/OutreachContacts";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GmailThread } from "schemas/dashboard";
import {
  EmailAddress,
  EmailDraft,
  EmailThreadSetting,
  Message,
} from "schemas/email";
import { routes } from "schemas/routes";

import Drawer from "components/Drawer";
import EmailDraftComponent from "components/EmailThread/EmailDraft";
import SendButtonGroup from "components/EmailThread/EmailDraft/SendButtonGroup";
import {
  generateDefaultTime,
  hasInvalidEmail,
} from "components/EmailThread/EmailDraft/utils";
import { getPageName } from "constants/trackingProps";
import { fetcherAuth } from "utils/api";
import { isInvalidEmail } from "utils/string";

import styles from "./styles";

const emptyDraft: EmailDraft = {
  toContacts: [],
  ccContacts: [],
  body: "",
  subject: "",
};

export default function ActionItemFollowUpDrawer() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { outreachContactId: id, threadId } = useParams();
  const outreachContactId = Number(id);

  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { currentOrg, currentUser } = useContext(OrganizationUserContext);
  const { currentContact, fetchContact, fetchEmailThreads } = useContext(
    OutreachContactsContext,
  );
  const { followupTemplates } = useContext(OutreachTemplatesContext);
  const {
    emailHealth,
    setOpenIntegrationDialog,
    fetchIntegrationHealth,
    userIntegration,
  } = useContext(UserIntegrationsContext);
  const { updateActionItems } = useContext(ActionItemsContext);

  const [gmailThread, setGmailThread] = useState<GmailThread | undefined>();
  const [emailDraft, setEmailDraft] = useState<EmailDraft>(emptyDraft);
  const [emailThreadSetting, setEmailThreadSetting] =
    useState<EmailThreadSetting>({ disableFollowUpTasks: false });
  const [loading, setLoading] = useState<boolean>(true);
  const [sendLoading, setSendLoading] = useState<boolean>(false);
  const [missingIntegration, setMissingIntegration] = useState<boolean>(false);
  const [scheduleMode, setScheduleMode] = useState<boolean>(false);

  const closeDrawer = () => {
    navigate(`/${routes.home}`);
  };

  const viewThread = () => {
    navigate(`/${routes.inbox}/inbox/${outreachContactId}/email/${threadId}`);
  };

  const fetchEmailThread = async () => {
    if (!currentOrg) {
      return;
    }
    setLoading(true);
    setGmailThread(undefined);
    setEmailThreadSetting({ disableFollowUpTasks: false });

    try {
      const pageName = getPageName();
      const threadViewParams = pageName
        ? `?source=${encodeURIComponent(pageName)}`
        : "";
      const {
        gmailThread,
        emailDraft: savedDraft,
        emailThreadSetting,
      } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/outreach-contacts/${outreachContactId}/threads/${threadId}${threadViewParams}`,
        "GET",
      );
      setGmailThread(gmailThread);
      setEmailThreadSetting(emailThreadSetting);
      if (savedDraft) {
        setEmailDraft(savedDraft);
      } else {
        const recipients = getReplyRecipients(
          gmailThread?.messages,
          userIntegration?.email,
        );
        setEmailDraft({
          toContacts: recipients?.to ?? [
            {
              name: currentContact?.contactName,
              email: currentContact?.email || "",
            },
          ],
          ccContacts: [],
          subject: gmailThread?.messages[0]?.subject
            ? `Re: ${gmailThread?.messages[0]?.subject}`
            : "",
          body: "",
        });
      }
    } catch (error) {
      if (error?.message?.includes("Google integration")) {
        setMissingIntegration(true);
      } else {
        setErrorAlert(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendDraftEmail = async (scheduleAt?: null | string) => {
    if (!emailHealth) {
      setOpenIntegrationDialog(true);
      return;
    }

    if (hasInvalidEmail(emailDraft)) {
      setAlert(
        "Some of the entered email addresses were invalid. Please make sure they are properly formatted.",
        "error",
      );
      return;
    }

    try {
      setSendLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/outreach-contacts/${outreachContactId}/send-gmail-message`,
        "POST",
        {},
        {
          ...emailDraft,
          scheduleAt,
          emailDraftId: emailDraft.id,
          gmailThreadId: emailDraft?.gmailThreadId || gmailThread?.id,
          disableFollowUpTasks: emailThreadSetting.disableFollowUpTasks,
          sendFrom: getPageName(),
        },
      );
      setAlert(
        scheduleAt
          ? "Successfully scheduled email!"
          : "Successfully sent email!",
        "success",
      );
      if (res.completedActionItemIds) {
        updateActionItems(res.completedActionItemIds);
      }
      fetchEmailThreads(outreachContactId, true);
      fetchContact(outreachContactId);
      closeDrawer();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSendLoading(false);
    }
  };

  useEffect(() => {
    if (isNaN(outreachContactId)) {
      return;
    }
    fetchContact(outreachContactId);
    fetchEmailThread();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outreachContactId, threadId, currentOrg?.id]);

  useEffect(() => {
    if (currentOrg?.id) {
      fetchIntegrationHealth(currentOrg?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const brandName =
    currentContact?.bentoBrand?.brandName ||
    currentContact?.brand?.name ||
    "this brand";
  const messageCount =
    gmailThread?.messages?.length || currentContact?.lastGmailThreadLength || 0;
  const threadLabel = `${messageCount} ${
    messageCount === 1 ? "message" : "messages"
  } in thread`;
  const showAiTip = followupTemplates.length > 0;

  return (
    <Drawer
      open={true}
      onClose={closeDrawer}
      width={{ xs: "auto", md: 560 }}
      sx={styles.drawerPaper}
    >
      <Box sx={styles.root}>
        <Box sx={styles.header}>
          <Box sx={styles.headerBadge}>
            <Box component="i" className="fa-regular fa-reply" />
          </Box>
          <Box>
            <Typography variant="h1" sx={styles.headerTitle}>
              Send follow-up
            </Typography>
            <Typography variant="body2" sx={styles.headerSub}>
              To {brandName} · {threadLabel}
            </Typography>
          </Box>
          <IconButton sx={styles.closeBtn} onClick={closeDrawer}>
            <Box component="i" className="fa-regular fa-xmark" />
          </IconButton>
        </Box>

        <Box sx={styles.body}>
          <Box sx={styles.contextCard}>
            <Avatar
              src={currentContact?.bentoBrand?.logoUrl}
              sx={styles.avatar}
            >
              {brandName?.[0]?.toUpperCase()}
            </Avatar>
            <Box>
              <Box sx={styles.brandName}>{brandName}</Box>
              <Box sx={styles.contextMeta}>
                {currentContact?.email
                  ? `${currentContact.email} · ${threadLabel}`
                  : threadLabel}
              </Box>
            </Box>
            <Box sx={styles.threadLink} onClick={viewThread}>
              View thread{" "}
              <Box component="i" className="fa-solid fa-arrow-right" />
            </Box>
          </Box>

          {showAiTip && (
            <Box sx={styles.aiTip}>
              <Box sx={styles.aiTipIcon}>
                <Box
                  component="i"
                  className="fa-solid fa-wand-magic-sparkles"
                />
              </Box>
              <Box>
                <strong>Bento suggests:</strong> Reference their recent reply
                and add one specific timeline for your follow-up.{" "}
              </Box>
            </Box>
          )}

          {missingIntegration && (
            <Alert severity="error">
              Log in to Google on your settings page to continue using this
              feature
            </Alert>
          )}

          {loading && (
            <Box sx={styles.progressContainer}>
              <Skeleton animation="wave" variant="rounded" height={40} />
              <Skeleton
                animation="wave"
                variant="rounded"
                height={30}
                sx={{ mt: 2 }}
              />
              <Skeleton
                animation="wave"
                variant="rounded"
                height={260}
                sx={{ mt: 2 }}
              />
            </Box>
          )}

          {!loading && gmailThread && currentContact && currentUser && (
            <Box sx={styles.mailCard}>
              <EmailDraftComponent
                emailDraft={emailDraft}
                gmailThread={gmailThread}
                setEmailDraft={setEmailDraft}
                handleSendDraftEmail={handleSendDraftEmail}
                from={{
                  email: userIntegration?.email || currentUser.email,
                  name: currentUser.name,
                }}
                cancelDraft={closeDrawer}
                sendLoading={sendLoading}
                displaySubjectEditor={!gmailThread.messages?.length}
                emailThreadSetting={emailThreadSetting}
                setEmailThreadSetting={setEmailThreadSetting}
                currentContact={currentContact}
                followUpDesign
              />
            </Box>
          )}
        </Box>

        {!loading && gmailThread && currentContact && (
          <Box sx={styles.footer}>
            <Box sx={styles.sendsNowPill}>
              <Box component="i" className="fa-regular fa-calendar" />
              {scheduleMode ? "Schedule later" : "Sends now"}
            </Box>
            <Box sx={{ flex: 1 }} />
            <Button sx={styles.cancelBtn} onClick={closeDrawer}>
              Cancel
            </Button>
            <SendButtonGroup
              handleSendDraftEmail={handleSendDraftEmail}
              sendLoading={sendLoading}
              defaultTime={generateDefaultTime(gmailThread)}
              buttonText="follow-up"
              disabled={
                emailDraft.body?.length < 10 ||
                emailDraft.subject?.trim()?.length < 5
              }
              buttonTheme={styles.sendBtn}
              arrowTheme={styles.sendArrow}
              onModeChange={setScheduleMode}
            />
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

function filterValidRecipients(
  contacts: EmailAddress[] | undefined,
  userEmail: string | undefined,
  exclude?: EmailAddress[],
): EmailAddress[] {
  const excludedEmails = new Set(exclude?.map((c) => c.email));
  return (
    contacts?.filter(
      (c) =>
        !isInvalidEmail(c.email || "") &&
        c.email !== userEmail &&
        !excludedEmails.has(c.email),
    ) || []
  );
}

function getReplyRecipients(
  messages: Message[] | undefined,
  userEmail: string | undefined,
): { to: EmailAddress[]; cc: EmailAddress[] } | null {
  if (!messages || messages.length === 0) {
    return null;
  }

  const lastMessage = messages[messages.length - 1];
  if (lastMessage.is_sent_by_user) {
    const to = filterValidRecipients(lastMessage.to, userEmail);
    return { to, cc: filterValidRecipients(lastMessage.cc, userEmail, to) };
  }

  return { to: [lastMessage.from_], cc: [] };
}
